/*
 * Akomoda — Customer Billing Portal
 * ---------------------------------------------------------------
 * Read-only, Hotel-facing SaaS billing dashboard. Reuses existing
 * subscription/invoices/payments logic — never mutates billing state.
 *
 * Route:  /settings/subscription
 * Roles:  hotel_admin / property_manager / finance_staff
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { useAuth } from "@/contexts/AuthContext";
import {
    Wallet, Download, FileText, CheckCircle2, AlertTriangle, Clock,
    ShieldAlert, Sparkles, Package, Percent, Coins, Building2,
    ArrowUpRight, Phone, Mail, Rocket, PlusCircle, Search,
    ChevronRight, MessageSquare, Timer, Ban, XCircle, Zap,
    Info, RefreshCw,
} from "lucide-react";


// ─────────────────────────── helpers ───────────────────────────
const money = (v, cur = "EUR") =>
    new Intl.NumberFormat("en-GB", {
        style: "currency", currency: cur,
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(v || 0));

const dateEU = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit", month: "2-digit", year: "numeric",
        });
    } catch { return "—"; }
};

const shortDT = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
};

const STATUS_META = {
    trial:         { color: "bg-blue-50 text-blue-700 border-blue-200",       icon: Sparkles,   label: "Trial" },
    active:        { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Active" },
    past_due:      { color: "bg-amber-50 text-amber-700 border-amber-200",     icon: Clock,      label: "Past Due" },
    grace_period:  { color: "bg-orange-50 text-orange-700 border-orange-200",  icon: AlertTriangle, label: "Grace Period" },
    restricted:    { color: "bg-red-50 text-red-700 border-red-200",           icon: ShieldAlert, label: "Restricted" },
    suspended:     { color: "bg-red-50 text-red-700 border-red-200",           icon: Ban,        label: "Suspended" },
    cancelled:     { color: "bg-muted text-muted-foreground border-border",    icon: XCircle,    label: "Cancelled" },
    expired:       { color: "bg-muted text-muted-foreground border-border",    icon: XCircle,    label: "Expired" },
    paid:          { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: "Paid" },
    open:          { color: "bg-amber-50 text-amber-700 border-amber-200",     icon: Clock,      label: "Open" },
    partially_paid:{ color: "bg-blue-50 text-blue-700 border-blue-200",        icon: Clock,      label: "Partial" },
    draft:         { color: "bg-muted/40 text-muted-foreground border-border",     icon: FileText,   label: "Draft" },
    refunded:      { color: "bg-muted/40 text-muted-foreground border-border",     icon: RefreshCw,  label: "Refunded" },
};

function StatusPill({ status }) {
    const m = STATUS_META[status] || { color: "bg-muted/40 text-foreground border-border", icon: Info, label: status };
    const Icon = m.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${m.color}`}>
            <Icon size={11} strokeWidth={2.5} /> {m.label}
        </span>
    );
}


// ═══════════════════════════════ MAIN PAGE ═══════════════════════════════
export default function BillingPortal() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [data, setData] = useState({
        overview: null, banners: [], charges: null, rooms: null,
        activeModules: [], availableModules: [], invoices: [],
        payments: [], discounts: null, timeline: [], company: null,
        paymentMethod: null,
    });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ status: "", q: "" });
    const [contactOpen, setContactOpen] = useState(false);
    const [dunningContext, setDunningContext] = useState(null);   // { invoice, ... }
    const [saveCardOpen, setSaveCardOpen] = useState(false);

    const load = async () => {
        try {
            const [ov, bn, ch, rm, mA, mAv, inv, pay, dc, tl, co, pm] = await Promise.all([
                api.get("/customer-billing/overview"),
                api.get("/customer-billing/banners"),
                api.get("/customer-billing/charges"),
                api.get("/customer-billing/rooms"),
                api.get("/customer-billing/modules/active"),
                api.get("/customer-billing/modules/available"),
                api.get("/customer-billing/invoices"),
                api.get("/customer-billing/payments"),
                api.get("/customer-billing/discounts-credits"),
                api.get("/customer-billing/timeline"),
                api.get("/customer-billing/company"),
                api.get("/customer-billing/payment-method"),
            ]);
            setData({
                overview: ov.data, banners: bn.data.banners || [], charges: ch.data,
                rooms: rm.data, activeModules: mA.data, availableModules: mAv.data,
                invoices: inv.data, payments: pay.data, discounts: dc.data,
                timeline: tl.data, company: co.data, paymentMethod: pm.data,
            });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    // Deep-link from dunning email: /settings/subscription?action=save-card&token=…
    useEffect(() => {
        const q = new URLSearchParams(window.location.search);
        if (q.get("action") !== "save-card") return;
        const token = q.get("token");
        const openModal = (ctx) => {
            setDunningContext(ctx);
            setSaveCardOpen(true);
            // Clean the URL after opening
            const clean = window.location.pathname;
            window.history.replaceState({}, "", clean);
        };
        if (!token) return openModal(null);
        api.get(`/customer-billing/dunning/resolve?token=${encodeURIComponent(token)}`)
            .then(({ data }) => openModal(data))
            .catch(() => {
                toast.error("This link is invalid or has expired.");
                openModal(null);
            });
    }, []);

    const filteredInvoices = useMemo(() => {
        let list = data.invoices || [];
        if (filter.status) {
            if (filter.status === "outstanding")
                list = list.filter((i) => ["open", "past_due", "partially_paid"].includes(i.status));
            else if (filter.status === "paid")
                list = list.filter((i) => i.status === "paid");
            else list = list.filter((i) => i.status === filter.status);
        }
        if (filter.q) {
            const q = filter.q.toLowerCase();
            list = list.filter((i) =>
                (i.invoice_number || "").toLowerCase().includes(q) ||
                (i.period_key || "").toLowerCase().includes(q) ||
                String(i.total || "").includes(q)
            );
        }
        return list;
    }, [data.invoices, filter]);

    if (loading) return (
        <>
            <Topbar title={t("billing_portal.title", "Subscription & Billing")} />
            <div className="flex-1 overflow-auto p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-40 rounded-lg bg-muted" />
                    <div className="grid grid-cols-3 gap-4">
                        <div className="h-24 rounded-lg bg-muted" />
                        <div className="h-24 rounded-lg bg-muted" />
                        <div className="h-24 rounded-lg bg-muted" />
                    </div>
                </div>
            </div>
        </>
    );

    const currency = data.overview?.billing_currency || "EUR";
    const sub = data.overview?.subscription;
    const isTrial = sub?.status === "trial";
    const isGrace = sub?.status === "grace_period";
    const isRestricted = sub?.status === "restricted";

    return (
        <>
            <Topbar
                title={t("billing_portal.title", "Subscription & Billing")}
                subtitle={data.overview?.hotel?.name}
                actions={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={load}
                            className="akm-btn-ghost text-[11px]"
                            data-testid="billing-portal-refresh"
                        >
                            <RefreshCw size={12} /> Refresh
                        </button>
                        <Link to="/settings" className="akm-btn-ghost text-[11px]" data-testid="back-to-settings">
                            ← Settings
                        </Link>
                    </div>
                }
            />
            <div className="flex-1 overflow-auto bg-muted/40 p-4 sm:p-6" data-testid="billing-portal">
                <div className="mx-auto max-w-7xl space-y-6">
                    {/* Banners */}
                    <BannerStack banners={data.banners} onContact={() => setContactOpen(true)} sub={sub} currency={currency} outstanding={data.overview?.outstanding_amount} />

                    {/* 1. Overview */}
                    <OverviewCard
                        data={data.overview}
                        currency={currency}
                        onContactSales={() => setContactOpen(true)}
                    />

                    {/* 2 & 3 side-by-side */}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                        <div className="lg:col-span-3 space-y-6">
                            <ChargesCard charges={data.charges} currency={currency} />
                            <BillableRoomsCard rooms={data.rooms} currency={currency} />
                            <InvoicesSection
                                invoices={filteredInvoices}
                                filter={filter} setFilter={setFilter}
                                currency={currency}
                            />
                            <PaymentsSection payments={data.payments} currency={currency} />
                            <TimelineSection events={data.timeline} />
                        </div>

                        <div className="lg:col-span-2 space-y-6">
                            <ActiveModulesCard modules={data.activeModules} currency={currency} />
                            <DiscountsCreditsCard discounts={data.discounts} currency={currency} />
                            <PaymentMethodCard pm={data.paymentMethod} onContact={() => setContactOpen(true)} onReload={load} />
                            <BillingCompanyCard company={data.company} />
                            <ContactSalesCard onOpen={() => setContactOpen(true)} />
                        </div>
                    </div>

                    {/* 11. Available modules — full width */}
                    <StatementsSection currency={currency} />
                    <AvailableModulesSection modules={data.availableModules} onReload={load} currency={currency} />
                </div>
            </div>

            {contactOpen && <ContactSalesModal onClose={() => setContactOpen(false)} onSubmit={load} />}
            {saveCardOpen && (
                <SaveCardModal
                    dunningContext={dunningContext}
                    onClose={() => { setSaveCardOpen(false); setDunningContext(null); }}
                    onSaved={() => { setSaveCardOpen(false); setDunningContext(null); load(); }}
                />
            )}
        </>
    );
}


// ═══════════════════════════════ BANNER STACK ═══════════════════════════════
function BannerStack({ banners, onContact, sub, currency, outstanding }) {
    if (!banners?.length) return null;
    return (
        <div className="space-y-2" data-testid="billing-banners">
            {banners.map((b, i) => {
                const severity = b.severity || "info";
                const bg = severity === "danger" ? "bg-red-50 border-red-300 text-red-900" :
                           severity === "warning" ? "bg-amber-50 border-amber-300 text-amber-900" :
                           "bg-blue-50 border-blue-300 text-blue-900";
                const Icon = severity === "danger" ? ShieldAlert : severity === "warning" ? AlertTriangle : Info;
                const messages = {
                    trial_ending: `Your trial ends in ${b.days_remaining} day${b.days_remaining === 1 ? "" : "s"} (${dateEU(b.ends_at)}). Upgrade to keep your workspace after that date.`,
                    past_due: `Your subscription is past due. Please settle the outstanding balance to avoid service restrictions.`,
                    grace_period: `You are in the grace period. Your access will be restricted after ${dateEU(b.grace_ends_at)} unless payment is received.`,
                    restricted: `Your subscription is restricted. Access to paid modules is limited until payment is received.`,
                    suspended: `Your subscription is suspended. Contact billing to restore service.`,
                    invoice_due: `You have ${b.count} outstanding invoice${b.count === 1 ? "" : "s"} totaling ${money(b.amount, currency)}.`,
                };
                return (
                    <div key={i}
                         className={`flex items-start justify-between gap-4 rounded-lg border px-4 py-3 ${bg}`}
                         data-testid={`banner-${b.kind}`}>
                        <div className="flex items-start gap-3">
                            <Icon size={18} className="mt-0.5 shrink-0" />
                            <div className="text-[13px] font-medium leading-snug">
                                {messages[b.kind] || b.kind}
                            </div>
                        </div>
                        <button onClick={onContact}
                                className="shrink-0 rounded-md border border-current bg-card/60 px-3 py-1 text-[11px] font-semibold hover:bg-card"
                                data-testid={`banner-${b.kind}-cta`}>
                            {b.kind === "trial_ending" ? "Upgrade" : b.kind.includes("due") || b.kind === "past_due" || b.kind === "grace_period" ? "Contact billing" : "Contact us"}
                        </button>
                    </div>
                );
            })}
        </div>
    );
}


// ═══════════════════════════════ OVERVIEW ═══════════════════════════════
function OverviewCard({ data, currency, onContactSales }) {
    const sub = data?.subscription;
    if (!sub) return (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="overview-empty">
            <Package size={40} className="mx-auto text-muted-foreground" />
            <div className="mt-3 text-lg font-semibold">No active subscription</div>
            <p className="mt-1 text-sm text-muted-foreground">Contact Akomoda sales to activate your subscription.</p>
            <button onClick={onContactSales} className="akm-btn-primary mt-4" data-testid="overview-contact-sales">Contact Sales</button>
        </div>
    );

    const trialText = data.trial_remaining_days != null
        ? `${data.trial_remaining_days} day${data.trial_remaining_days === 1 ? "" : "s"} left`
        : null;

    return (
        <div className="rounded-2xl border border-border bg-card shadow-sm" data-testid="subscription-overview">
            <div className="border-b border-border p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                            <Wallet size={12} /> Subscription overview
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                            <div className="text-3xl font-bold tracking-tight text-foreground" data-testid="overview-recurring-total">
                                {money(sub.recurring_total, currency)}
                                <span className="ml-1 text-[13px] font-normal text-muted-foreground">/ {sub.billing_cycle}</span>
                            </div>
                            <StatusPill status={sub.status} />
                        </div>
                        <div className="mt-1 flex flex-wrap gap-2 text-[12px] text-muted-foreground">
                            <span>Plan: <strong>{data.plan?.name || "Custom"}</strong></span>
                            <span>·</span>
                            <span>Cycle: <strong className="capitalize">{sub.billing_cycle}</strong></span>
                            <span>·</span>
                            <span>Pricing version: <strong>{data.pricing_version?.name || "—"}</strong></span>
                            <span>·</span>
                            <span>Currency: <strong>{sub.currency || currency}</strong></span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {data.latest_invoice_id && (
                            <a
                                href={`${API_BASE}/customer-billing/invoices/${data.latest_invoice_id}/pdf`}
                                target="_blank" rel="noreferrer"
                                className="akm-btn-primary text-[12px]"
                                data-testid="overview-download-latest-invoice">
                                <Download size={12} /> Latest invoice
                            </a>
                        )}
                        <button onClick={onContactSales}
                                className="akm-btn-ghost text-[12px]"
                                data-testid="overview-request-upgrade">
                            <ArrowUpRight size={12} /> Request upgrade
                        </button>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-px bg-muted sm:grid-cols-4">
                <MetaCell label="Next billing" value={dateEU(sub.next_billing_at)} icon={Clock} />
                <MetaCell label={sub.status === "trial" ? "Trial ends" : "Contract start"}
                          value={sub.status === "trial" ? dateEU(sub.trial_ends_at) : dateEU(sub.current_period_start)}
                          icon={Sparkles}
                          highlight={sub.status === "trial"} />
                {sub.status === "trial" && trialText && (
                    <MetaCell label="Trial remaining" value={trialText} icon={Timer} highlight />
                )}
                {sub.status === "grace_period" && data.grace_remaining_days != null && (
                    <MetaCell label="Grace remaining" value={`${data.grace_remaining_days} days`} icon={AlertTriangle} highlight />
                )}
                <MetaCell label="Rooms billed" value={sub.billable_room_count} icon={Building2} />
                {data.outstanding_amount && Number(data.outstanding_amount) > 0 && (
                    <MetaCell label="Outstanding" value={money(data.outstanding_amount, currency)} icon={Coins} highlight />
                )}
            </div>
        </div>
    );
}

function MetaCell({ label, value, icon: Icon, highlight }) {
    return (
        <div className={`bg-card p-4 ${highlight ? "bg-amber-50/50" : ""}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {Icon && <Icon size={10} />} {label}
            </div>
            <div className={`mt-1 text-[15px] font-semibold ${highlight ? "text-amber-800" : "text-foreground"}`}>{value}</div>
        </div>
    );
}


// ═══════════════════════════════ CHARGES ═══════════════════════════════
function ChargesCard({ charges, currency }) {
    if (!charges?.lines?.length) return null;
    return (
        <div className="rounded-2xl border border-border bg-card shadow-sm" data-testid="current-charges">
            <div className="flex items-center justify-between border-b border-border p-5">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Zap size={12} className="inline -mt-0.5 mr-1" /> Current charges
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-foreground">Recurring monthly total</h3>
                </div>
                <div className="text-right">
                    <div className="text-[10px] font-semibold uppercase text-muted-foreground">Billed per {charges.billing_cycle}</div>
                    <div className="text-2xl font-bold text-foreground">{money(charges.recurring_total, currency)}</div>
                </div>
            </div>
            <div className="divide-y divide-slate-100">
                {charges.lines.map((line) => (
                    <div key={line.id} className="flex items-center justify-between px-5 py-3 hover:bg-muted/40" data-testid={`charge-line-${line.kind}`}>
                        <div className="flex-1">
                            <div className="text-[13px] font-medium text-foreground">{line.description}</div>
                            {line.quantity != null && line.unit_price && (
                                <div className="mt-0.5 text-[11px] text-muted-foreground">
                                    {line.quantity} × {money(line.unit_price, currency)}
                                </div>
                            )}
                        </div>
                        <div className={`text-[13px] font-semibold ${line.kind === "discount" ? "text-emerald-600" : "text-foreground"}`}>
                            {line.kind === "discount" ? line.subtotal : money(line.subtotal, currency)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


// ═══════════════════════════════ BILLABLE ROOMS ═══════════════════════════════
function BillableRoomsCard({ rooms, currency }) {
    if (!rooms) return null;
    const isOverride = rooms.room_count_source === "override";
    return (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="billable-rooms">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Building2 size={12} className="inline -mt-0.5 mr-1" /> Billable rooms
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-foreground">Room pricing calculation</h3>
                </div>
                {isOverride && (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                        Manual override
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <MetricTile label="Billable rooms" value={rooms.billable_room_count} />
                <MetricTile label="Tier range" value={rooms.tier ? `${rooms.tier.from_rooms}–${rooms.tier.to_rooms || "∞"}` : "—"} />
                <MetricTile label="Price / room" value={rooms.tier ? money(rooms.tier.price_per_room, currency) : "—"} />
                <MetricTile label="Platform charge" value={money(rooms.platform_charge, currency)} highlight />
            </div>
            {isOverride && (
                <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
                    <Info size={14} className="mt-0.5 shrink-0" />
                    <div>
                        Your room count has been adjusted by Akomoda ({rooms.room_count_override_reason || "custom agreement"}).
                        The automatic count would be <strong>{rooms.auto_billable_room_count}</strong>.
                    </div>
                </div>
            )}
        </div>
    );
}

function MetricTile({ label, value, highlight }) {
    return (
        <div className={`rounded-lg border p-3 ${highlight ? "border-primary/30 bg-primary/5" : "border-border bg-muted/40"}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
            <div className={`mt-1 text-lg font-bold ${highlight ? "text-primary" : "text-foreground"}`}>{value}</div>
        </div>
    );
}


// ═══════════════════════════════ INVOICES ═══════════════════════════════
function InvoicesSection({ invoices, filter, setFilter, currency }) {
    return (
        <div className="rounded-2xl border border-border bg-card shadow-sm" data-testid="invoices-section">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-5">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <FileText size={12} className="inline -mt-0.5 mr-1" /> Invoice history
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-foreground">{invoices.length} invoice{invoices.length === 1 ? "" : "s"}</h3>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={filter.q}
                            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                            placeholder="Search…"
                            className="akm-input py-1 pl-7 pr-2 text-[12px]"
                            data-testid="invoice-search-input"
                        />
                    </div>
                    <select
                        value={filter.status}
                        onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                        className="akm-input py-1 text-[12px]"
                        data-testid="invoice-status-filter"
                    >
                        <option value="">All statuses</option>
                        <option value="outstanding">Outstanding</option>
                        <option value="paid">Paid</option>
                        <option value="open">Open</option>
                        <option value="past_due">Past due</option>
                        <option value="partially_paid">Partially paid</option>
                    </select>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                    <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="py-3 pl-5">Invoice</th>
                            <th>Period</th>
                            <th>Issued</th>
                            <th>Status</th>
                            <th className="text-right">Total</th>
                            <th className="text-right">Paid</th>
                            <th className="text-right">Outstanding</th>
                            <th className="pr-5"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoices.map((i) => (
                            <tr key={i.id} className="border-t border-border hover:bg-muted/40" data-testid={`invoice-row-${i.invoice_number}`}>
                                <td className="py-3 pl-5 font-mono font-semibold text-foreground">{i.invoice_number}</td>
                                <td className="text-muted-foreground">{i.period_key || "—"}</td>
                                <td className="text-muted-foreground">{dateEU(i.issued_at || i.created_at)}</td>
                                <td><StatusPill status={i.status} /></td>
                                <td className="text-right font-semibold text-foreground">{money(i.total, i.currency || currency)}</td>
                                <td className="text-right text-emerald-700">{money(i.amount_paid, i.currency || currency)}</td>
                                <td className={`text-right font-semibold ${Number(i.amount_due) > 0 ? "text-red-700" : "text-muted-foreground"}`}>
                                    {money(i.amount_due, i.currency || currency)}
                                </td>
                                <td className="pr-5">
                                    <a
                                        href={`${API_BASE}/customer-billing/invoices/${i.id}/pdf`}
                                        target="_blank" rel="noreferrer"
                                        className="akm-btn-ghost text-[11px]"
                                        data-testid={`invoice-download-${i.invoice_number}`}>
                                        <Download size={11} /> PDF
                                    </a>
                                </td>
                            </tr>
                        ))}
                        {invoices.length === 0 && (
                            <tr>
                                <td colSpan={8} className="py-10 text-center text-muted-foreground">No invoices found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// ═══════════════════════════════ PAYMENTS ═══════════════════════════════
function PaymentsSection({ payments, currency }) {
    return (
        <div className="rounded-2xl border border-border bg-card shadow-sm" data-testid="payments-section">
            <div className="border-b border-border p-5">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Coins size={12} className="inline -mt-0.5 mr-1" /> Payment history
                </div>
                <h3 className="mt-1 text-base font-semibold text-foreground">{payments.length} payment{payments.length === 1 ? "" : "s"}</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                    <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                        <tr>
                            <th className="py-3 pl-5">Date</th>
                            <th>Method</th>
                            <th>Reference</th>
                            <th>Invoice</th>
                            <th>Status</th>
                            <th className="pr-5 text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payments.map((p) => (
                            <tr key={p.id} className="border-t border-border hover:bg-muted/40">
                                <td className="py-3 pl-5 text-foreground">{shortDT(p.created_at)}</td>
                                <td className="capitalize text-foreground">{p.method}</td>
                                <td className="font-mono text-[11px] text-muted-foreground">{p.reference || "—"}</td>
                                <td className="font-mono text-[11px] text-foreground">{p.invoice_number || "—"}</td>
                                <td><StatusPill status={p.status === "succeeded" ? "paid" : p.status} /></td>
                                <td className="pr-5 text-right font-semibold text-emerald-700">
                                    {money(p.amount, p.currency || currency)}
                                </td>
                            </tr>
                        ))}
                        {payments.length === 0 && (
                            <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No payments recorded yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// ═══════════════════════════════ TIMELINE ═══════════════════════════════
const TIMELINE_LABELS = {
    "subscription.start": "Subscription started",
    "subscription.update": "Subscription updated",
    "subscription.rooms_override": "Room count adjusted",
    "subscription.status_transition": "Status transition",
    "invoice.generate": "Invoice generated",
    "invoice.payment": "Payment recorded",
    "invoice.pdf_generated": "Invoice downloaded",
    "invoice.email_queued": "Invoice emailed",
    "discount.create": "Discount applied",
    "credit.create": "Credit added",
    "module.enable": "Module activated",
    "module.disable": "Module disabled",
    "module.cascade_disable": "Module disabled (cascade)",
    "module.activation_requested": "Activation requested",
    "billing.contact_sales": "Contacted sales",
    "billing.upgrade_requested": "Upgrade requested",
};

function TimelineSection({ events }) {
    if (!events?.length) return null;
    return (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="billing-timeline">
            <div className="mb-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Clock size={12} className="inline -mt-0.5 mr-1" /> Billing timeline
                </div>
                <h3 className="mt-1 text-base font-semibold text-foreground">Recent events</h3>
            </div>
            <div className="space-y-3">
                {events.slice(0, 20).map((e) => (
                    <div key={e.id} className="flex items-start gap-3 border-l-2 border-border pl-4" data-testid={`timeline-event-${e.action}`}>
                        <div className="-ml-[19px] mt-1 h-3 w-3 rounded-full border-2 border-primary bg-card" />
                        <div className="flex-1">
                            <div className="text-[12.5px] font-medium text-foreground">{TIMELINE_LABELS[e.action] || e.action}</div>
                            <div className="mt-0.5 text-[11px] text-muted-foreground">
                                {shortDT(e.created_at)}
                                {e.user_email && <span> · {e.user_email}</span>}
                                {e.metadata?.to && (
                                    <span> · <span className="font-semibold text-foreground">{e.metadata.from || "—"} → {e.metadata.to}</span></span>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


// ═══════════════════════════════ ACTIVE MODULES ═══════════════════════════════
function ActiveModulesCard({ modules, currency }) {
    const grouped = useMemo(() => {
        const g = {};
        for (const m of modules || []) {
            const k = m.category || "other";
            (g[k] = g[k] || []).push(m);
        }
        return g;
    }, [modules]);
    return (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="active-modules">
            <div className="mb-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Package size={12} className="inline -mt-0.5 mr-1" /> Active modules
                </div>
                <h3 className="mt-1 text-base font-semibold text-foreground">{modules.length} enabled</h3>
            </div>
            <div className="space-y-4">
                {Object.entries(grouped).map(([cat, list]) => (
                    <div key={cat}>
                        <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{cat.replace(/_/g, " ")}</div>
                        <div className="space-y-2">
                            {list.map((m) => (
                                <div key={m.key}
                                     className="flex items-center justify-between rounded-lg border border-border bg-muted/40/50 p-3"
                                     data-testid={`active-module-${m.key}`}>
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-[13px] font-semibold text-foreground">{m.name}</div>
                                        <div className="mt-0.5 flex items-center gap-1.5 text-[10px] uppercase text-muted-foreground">
                                            {m.is_core
                                                ? <span className="rounded-full bg-slate-200 px-2 py-0.5 font-semibold text-foreground">Included</span>
                                                : m.status === "trial"
                                                    ? <span className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-800">Trial</span>
                                                    : <span className="rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary">Paid add-on</span>}
                                            {m.trial_ends_at && <span>· Ends {dateEU(m.trial_ends_at)}</span>}
                                        </div>
                                    </div>
                                    <div className="ml-3 text-right">
                                        <div className="text-[12px] font-semibold text-foreground">
                                            {m.is_core || Number(m.monthly_cost || 0) === 0 ? "Included" : money(m.monthly_cost, m.currency || currency)}
                                        </div>
                                        {!m.is_core && Number(m.monthly_cost || 0) > 0 && (
                                            <div className="text-[10px] text-muted-foreground">/ month</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


// ═══════════════════════════════ DISCOUNTS & CREDITS ═══════════════════════════════
function DiscountsCreditsCard({ discounts, currency }) {
    const empty = !discounts?.discounts?.length && !discounts?.credits?.length;
    return (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="discounts-credits">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Percent size={12} className="inline -mt-0.5 mr-1" /> Discounts & credits
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-foreground">
                        {discounts?.credits?.length > 0 && `Credit balance: ${money(discounts.credit_balance, currency)}`}
                        {discounts?.credits?.length === 0 && "No active items"}
                    </h3>
                </div>
            </div>
            {empty ? (
                <div className="rounded-lg border border-dashed border-border py-6 text-center text-[12px] text-muted-foreground">
                    No active discounts or credits.
                </div>
            ) : (
                <div className="space-y-2">
                    {(discounts.discounts || []).map((d) => (
                        <div key={d.id} className="rounded-lg border border-emerald-100 bg-emerald-50 p-3" data-testid={`discount-${d.id}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-[13px] font-semibold text-emerald-900">{d.reason || "Discount"}</div>
                                    <div className="mt-0.5 text-[10px] uppercase text-emerald-700">
                                        {d.type === "percentage" ? `${d.percent}%` : `-${money(d.amount, currency)}`}
                                        {d.duration && ` · ${d.duration}`}
                                    </div>
                                </div>
                                {d.ends_at && <div className="text-[10px] text-emerald-800">until {dateEU(d.ends_at)}</div>}
                            </div>
                        </div>
                    ))}
                    {(discounts.credits || []).map((c) => (
                        <div key={c.id} className="rounded-lg border border-blue-100 bg-blue-50 p-3" data-testid={`credit-${c.id}`}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-[13px] font-semibold text-blue-900">{c.reason}</div>
                                    <div className="mt-0.5 text-[10px] uppercase text-blue-700">{c.kind}</div>
                                </div>
                                <div className="text-[13px] font-bold text-blue-900">{money(c.amount, currency)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


// ═══════════════════════════════ PAYMENT METHOD ═══════════════════════════════
function PaymentMethodCard({ pm, onContact, onReload }) {
    const [showSaveCard, setShowSaveCard] = useState(false);
    if (!pm) return null;
    // Backend now returns { method, configured, detail, stripe: {...} }
    const stripe = pm.stripe || {};
    const hasCard = !!stripe.default_payment_method_id;
    const brand = (stripe.card_brand || "").toUpperCase();
    const labels = { bank_transfer: "Bank Transfer", stripe: "Credit Card", paypal: "PayPal", manual: "Manual Invoice" };
    return (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="payment-method">
            <div className="flex items-start justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Wallet size={12} className="inline -mt-0.5 mr-1" /> Payment method
                </div>
                {stripe.configured && (
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[9px] font-bold uppercase text-blue-700">
                        Stripe {stripe.card_last4 ? "active" : "ready"}
                    </span>
                )}
            </div>
            {hasCard ? (
                <>
                    <div className="mt-3 flex items-center gap-2">
                        <div className="rounded-md bg-slate-900 px-2 py-1 text-[10px] font-bold text-white">{brand}</div>
                        <div className="text-[14px] font-semibold text-foreground">•••• {stripe.card_last4}</div>
                        <div className="text-[11px] text-muted-foreground">exp {stripe.card_exp}</div>
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button onClick={() => setShowSaveCard(true)}
                                className="akm-btn-ghost text-[11px]"
                                data-testid="update-card">
                            Update card
                        </button>
                    </div>
                </>
            ) : pm.configured ? (
                <>
                    <div className="mt-2 text-[15px] font-semibold text-foreground">{labels[pm.method] || pm.method}</div>
                    {pm.detail && <div className="mt-0.5 text-[12px] text-muted-foreground">{pm.detail}</div>}
                    {stripe.configured && (
                        <button onClick={() => setShowSaveCard(true)}
                                className="akm-btn-primary mt-3 w-full text-[11px]"
                                data-testid="save-card">
                            <PlusCircle size={11} /> Save a card
                        </button>
                    )}
                </>
            ) : (
                <>
                    <div className="mt-2 text-[13px] text-muted-foreground">No payment method configured.</div>
                    {stripe.configured ? (
                        <button onClick={() => setShowSaveCard(true)}
                                className="akm-btn-primary mt-3 w-full text-[11px]"
                                data-testid="save-card-cta">
                            <PlusCircle size={11} /> Save a card
                        </button>
                    ) : (
                        <button onClick={onContact}
                                className="akm-btn-ghost mt-3 text-[11px]"
                                data-testid="payment-method-contact">
                            <Mail size={11} /> Contact billing
                        </button>
                    )}
                </>
            )}
            {showSaveCard && <SaveCardModal onClose={() => setShowSaveCard(false)} onSaved={() => { setShowSaveCard(false); onReload(); }} />}
        </div>
    );
}


// ═══════════════════════════════ SAVE CARD MODAL (Stripe SetupIntent) ═══════════════════════════════
function SaveCardModal({ onClose, onSaved, dunningContext }) {
    const [phase, setPhase] = useState("loading"); // loading | ready | saving | done | error
    const [error, setError] = useState("");
    const [pk, setPk] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [nameOnCard, setNameOnCard] = useState("");
    const cardMountedRef = useRef(false);

    useEffect(() => {
        (async () => {
            try {
                // Load Stripe.js
                if (!window.Stripe) {
                    await new Promise((resolve, reject) => {
                        const s = document.createElement("script");
                        s.src = "https://js.stripe.com/v3/";
                        s.onload = resolve;
                        s.onerror = () => reject(new Error("stripe.js failed to load"));
                        document.head.appendChild(s);
                    });
                }
                const { data } = await api.post("/customer-billing/stripe/setup-intent");
                if (!data.client_secret) {
                    setError("Stripe is not configured for this workspace yet.");
                    setPhase("error");
                    return;
                }
                setPk(data.publishable_key);
                setClientSecret(data.client_secret);
                setPhase("ready");
            } catch (e) {
                setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                setPhase("error");
            }
        })();
    }, []);

    useEffect(() => {
        if (phase !== "ready" || cardMountedRef.current || !pk) return;
        cardMountedRef.current = true;
        const stripe = window.Stripe(pk);
        const elements = stripe.elements();
        const card = elements.create("card", {
            style: {
                base: {
                    fontSize: "15px",
                    color: "#0f172a",
                    fontFamily: "Inter, sans-serif",
                    "::placeholder": { color: "#94a3b8" },
                },
                invalid: { color: "#dc2626" },
            },
        });
        card.mount("#akm-card-element");
        window._akm_stripe = { stripe, card };
    }, [phase, pk]);

    const submit = async () => {
        setPhase("saving");
        setError("");
        try {
            const { stripe, card } = window._akm_stripe || {};
            const { setupIntent, error: err } = await stripe.confirmCardSetup(clientSecret, {
                payment_method: { card, billing_details: { name: nameOnCard } },
            });
            if (err) { setError(err.message); setPhase("ready"); return; }
            // Notify backend to persist as default
            await api.post("/customer-billing/stripe/attach-payment-method", {
                payment_method_id: setupIntent.payment_method,
            });
            setPhase("done");
            toast.success("Card saved securely with Stripe.");
            setTimeout(onSaved, 1200);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
            setPhase("ready");
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-2xl"
                 onClick={(e) => e.stopPropagation()} data-testid="save-card-modal">
                <div className="flex items-center gap-2">
                    <Wallet size={18} className="text-primary" />
                    <div className="text-lg font-bold text-foreground">Save a card</div>
                </div>
                <p className="mt-1 text-[12px] text-muted-foreground">
                    Your card is stored securely by Stripe. Akomoda never sees the full card number.
                </p>

                {dunningContext?.invoice && (
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="dunning-banner">
                        <div className="flex items-start gap-2">
                            <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-700" />
                            <div className="text-[11.5px] leading-relaxed text-amber-900">
                                <strong>Payment failed for invoice {dunningContext.invoice.invoice_number}.</strong> Attempt {dunningContext.invoice.charge_attempts}/3.
                                {dunningContext.invoice.last_charge_error && (
                                    <> Reason: <em>{dunningContext.invoice.last_charge_error}</em>.</>
                                )}
                                {" "}Save a new card to keep your subscription active.
                            </div>
                        </div>
                    </div>
                )}

                {phase === "loading" && (
                    <div className="my-8 text-center text-[13px] text-muted-foreground">Loading Stripe…</div>
                )}
                {phase === "error" && (
                    <div className="my-6 rounded-md bg-red-50 p-3 text-[12px] text-red-800">{error}</div>
                )}
                {(phase === "ready" || phase === "saving") && (
                    <>
                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="mb-1 block text-[11px] font-semibold uppercase text-muted-foreground">Name on card</label>
                                <input value={nameOnCard} onChange={(e) => setNameOnCard(e.target.value)}
                                       className="akm-input w-full" placeholder="Jane Doe"
                                       data-testid="save-card-name" />
                            </div>
                            <div>
                                <label className="mb-1 block text-[11px] font-semibold uppercase text-muted-foreground">Card details</label>
                                <div id="akm-card-element" className="rounded-md border border-border bg-card p-3" />
                            </div>
                            {error && <div className="rounded-md bg-red-50 p-2 text-[11px] text-red-700">{error}</div>}
                        </div>
                        <div className="mt-4 flex items-center gap-2 text-[10px] text-muted-foreground">
                            <ShieldAlert size={11} /> PCI-compliant · powered by Stripe
                        </div>
                    </>
                )}
                {phase === "done" && (
                    <div className="my-8 text-center">
                        <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
                        <div className="mt-2 text-[14px] font-semibold text-foreground">Card saved!</div>
                    </div>
                )}

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="akm-btn-ghost" data-testid="save-card-cancel">Cancel</button>
                    <button onClick={submit}
                            disabled={phase !== "ready" || !nameOnCard.trim()}
                            className="akm-btn-primary"
                            data-testid="save-card-submit">
                        {phase === "saving" ? "Saving…" : "Save card"}
                    </button>
                </div>
            </div>
        </div>
    );
}


// ═══════════════════════════════ BILLING COMPANY ═══════════════════════════════
function BillingCompanyCard({ company }) {
    if (!company) return null;
    const rows = [
        ["Trade name", company.trade_name],
        ["Legal name", company.legal_name],
        ["NIPT", company.nipt],
        ["VAT number", company.vat_id],
        ["Reg. no", company.business_reg_no],
        ["Country / City", `${company.country || "—"}${company.city ? ", " + company.city : ""}`],
        ["Address", `${company.address || "—"}${company.postal_code ? ", " + company.postal_code : ""}`],
        ["Email", company.contact_email],
        ["Phone", company.phone],
        ["IBAN", company.iban],
        ["SWIFT/BIC", company.swift_bic],
    ];
    return (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="billing-company">
            <div className="mb-3 flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Building2 size={12} className="inline -mt-0.5 mr-1" /> Billing company
                </div>
                <Link to="/settings" className="akm-btn-ghost text-[10px]" data-testid="edit-company">
                    Edit →
                </Link>
            </div>
            <div className="space-y-1.5">
                {rows.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3 text-[12px]">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-medium text-foreground text-right">{v || "—"}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}


// ═══════════════════════════════ CONTACT SALES CARD ═══════════════════════════════
function ContactSalesCard({ onOpen }) {
    return (
        <div className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/0 p-5 shadow-sm" data-testid="contact-sales-card">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
                <MessageSquare size={12} className="inline -mt-0.5 mr-1" /> Talk to us
            </div>
            <h3 className="mt-1 text-base font-semibold text-foreground">Contact Sales</h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
                Need to upgrade, add modules, or discuss an enterprise agreement? Our team responds within one business day.
            </p>
            <button onClick={onOpen}
                    className="akm-btn-primary mt-3 w-full text-[12px]"
                    data-testid="open-contact-sales-modal">
                <Rocket size={12} /> Start a conversation
            </button>
        </div>
    );
}


// ═══════════════════════════════ STATEMENTS ═══════════════════════════════
function StatementsSection({ currency }) {
    const [months, setMonths] = useState([]);
    useEffect(() => {
        api.get("/customer-billing/statements/available").then(({ data }) => setMonths(data));
    }, []);
    if (!months?.length) return null;
    return (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="statements-section">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <FileText size={12} className="inline -mt-0.5 mr-1" /> Monthly statements
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-foreground">Downloadable statements</h3>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                        Consolidated view of every invoice, payment and credit for the selected month.
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {months.map((m) => {
                    const [y, mo] = m.split("-");
                    const label = new Date(`${m}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
                    return (
                        <a key={m}
                           href={`${API_BASE}/customer-billing/statements/${y}/${parseInt(mo, 10)}/pdf`}
                           target="_blank" rel="noreferrer"
                           className="group flex items-center justify-between rounded-lg border border-border bg-muted/40 p-3 text-[12px] transition-colors hover:border-primary/30 hover:bg-primary/5"
                           data-testid={`statement-${m}`}>
                            <div>
                                <div className="text-[11px] font-semibold uppercase text-muted-foreground">Statement</div>
                                <div className="mt-0.5 text-[12.5px] font-semibold text-foreground">{label}</div>
                            </div>
                            <Download size={13} className="text-muted-foreground group-hover:text-primary" />
                        </a>
                    );
                })}
            </div>
        </div>
    );
}


// ═══════════════════════════════ AVAILABLE MODULES ═══════════════════════════════
function AvailableModulesSection({ modules, onReload, currency }) {
    const [busy, setBusy] = useState({});

    const request = async (mod) => {
        setBusy((s) => ({ ...s, [mod.key]: true }));
        try {
            await api.post("/customer-billing/request-activation", { module_key: mod.key });
            toast.success(`Requested "${mod.name}". Our team will contact you shortly.`);
            await onReload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy((s) => ({ ...s, [mod.key]: false }));
        }
    };

    if (!modules?.length) return null;
    const grouped = modules.reduce((acc, m) => {
        (acc[m.category || "other"] = acc[m.category || "other"] || []).push(m);
        return acc;
    }, {});

    return (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid="available-modules">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <PlusCircle size={12} className="inline -mt-0.5 mr-1" /> Available modules
                    </div>
                    <h3 className="mt-1 text-base font-semibold text-foreground">Expand your workspace</h3>
                </div>
                <span className="text-[11px] text-muted-foreground">{modules.length} available</span>
            </div>
            <div className="space-y-6">
                {Object.entries(grouped).map(([cat, list]) => (
                    <div key={cat}>
                        <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{cat.replace(/_/g, " ")}</div>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {list.map((m) => (
                                <div key={m.key}
                                     className="flex flex-col rounded-xl border border-border bg-muted/40/50 p-4 transition-shadow hover:shadow-md"
                                     data-testid={`available-module-${m.key}`}>
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="text-[13.5px] font-semibold text-foreground">{m.name}</div>
                                            {m.module_type === "enterprise" && (
                                                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase text-amber-800">Enterprise</span>
                                            )}
                                        </div>
                                        <p className="mt-1 line-clamp-2 text-[11.5px] text-muted-foreground">{m.description || "—"}</p>
                                    </div>
                                    <div className="mt-3 flex items-center justify-between">
                                        <div className="text-[11px] font-semibold text-foreground">
                                            {Number(m.monthly_price || 0) > 0 ? `${money(m.monthly_price, m.currency || currency)}/mo` : "Contact us"}
                                        </div>
                                        {m.requested ? (
                                            <span className="rounded-md bg-emerald-50 px-2.5 py-1 text-[10.5px] font-semibold text-emerald-800">
                                                <CheckCircle2 size={10} className="inline -mt-0.5" /> Requested
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() => request(m)}
                                                disabled={busy[m.key]}
                                                className="rounded-md border border-primary/30 bg-card px-2.5 py-1 text-[10.5px] font-semibold text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-50"
                                                data-testid={`request-activation-${m.key}`}>
                                                {busy[m.key] ? "…" : "Request activation"} <ChevronRight size={10} className="inline -mt-0.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


// ═══════════════════════════════ CONTACT SALES MODAL ═══════════════════════════════
function ContactSalesModal({ onClose, onSubmit }) {
    const [form, setForm] = useState({ kind: "upgrade", message: "", contact_email: "", contact_phone: "" });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await api.post("/customer-billing/contact-sales", form);
            toast.success("Message sent — Akomoda sales will reach out shortly.");
            onClose(); onSubmit();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div
                className="w-full max-w-md rounded-xl bg-card p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="contact-sales-modal"
            >
                <div className="text-lg font-bold text-foreground">Contact Akomoda</div>
                <p className="mt-1 text-[12px] text-muted-foreground">Tell us what you need — our team responds within 1 business day.</p>

                <div className="mt-4 space-y-3">
                    <div>
                        <label className="mb-1 block text-[11px] font-semibold uppercase text-muted-foreground">Type of request</label>
                        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                                className="akm-input w-full" data-testid="contact-sales-kind">
                            <option value="upgrade">Request an upgrade</option>
                            <option value="enterprise">Enterprise agreement</option>
                            <option value="additional_modules">Additional modules</option>
                            <option value="demo">Book a demo</option>
                            <option value="billing">Contact billing</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-[11px] font-semibold uppercase text-muted-foreground">Message</label>
                        <textarea rows={4} value={form.message}
                                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                                  placeholder="What would you like to discuss?"
                                  className="akm-input w-full"
                                  data-testid="contact-sales-message" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold uppercase text-muted-foreground">Email</label>
                            <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                                   className="akm-input w-full" placeholder="you@company.com"
                                   data-testid="contact-sales-email" />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold uppercase text-muted-foreground">Phone</label>
                            <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                                   className="akm-input w-full" placeholder="+355…"
                                   data-testid="contact-sales-phone" />
                        </div>
                    </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="akm-btn-ghost" data-testid="contact-sales-cancel">Cancel</button>
                    <button onClick={submit} disabled={busy} className="akm-btn-primary" data-testid="contact-sales-submit">
                        {busy ? "Sending…" : "Send message"}
                    </button>
                </div>
            </div>
        </div>
    );
}
